import { Button } from '@mui/joy';

import { Link } from 'react-router-dom';

import FewShotSearchEmptyState from '@/assets/fewShotSearch.svg?react';
import FewShotSearchEmptyStateDark from '@/assets/fewShotSearchDark.svg?react';
import { useColorScheme } from '@/hooks/useColorScheme';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { useGetSyncStatus } from '@/hooks/useSwr';
import { DatasetSchema } from '@/types/schema';

import { FewShotSearch } from './FewShotSearch';
import { FewShotSelector } from './FewShotSelector';

type Props = {
  datasetId: string;
  inputSchema: DatasetSchema['inputs_schema_definition'];
  onMutate: () => void;
  setEditDatasetOpen?: () => void;
};

const DisabledState = () => {
  const { isDarkMode } = useColorScheme();
  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center gap-4">
      <div className="z-10 mt-[300px] flex flex-col items-center justify-center gap-2 bg-background">
        <div className="text-md text-center font-semibold">
          Datasets 🤝 Few-Shot Prompting
        </div>
        <div className="text-md">
          Quickly and efficiently search your dataset for few shot examples.
        </div>
        <div className="mt-2 flex gap-2">
          <Link
            to={
              'https://docs.langchain.com/langsmith/index-datasets-for-dynamic-few-shot-example-selection'
            }
            className="text-sm "
            target="_blank"
          >
            <Button color="neutral" variant="outlined">
              Docs
            </Button>
          </Link>
          <Link
            to={'https://forms.gle/fFRjrdebZ2H2kg6o7'}
            rel="noreferrer noopener"
            className="text-sm "
            target="_blank"
          >
            <Button>Request access</Button>
          </Link>
        </div>
      </div>

      <div className="absolute top-0 z-0 flex items-center justify-center">
        {isDarkMode ? (
          <FewShotSearchEmptyStateDark />
        ) : (
          <FewShotSearchEmptyState className="stroke-black" />
        )}
      </div>
    </div>
  );
};

const FewShotSearchPanel = (props: Props) => {
  const { value: fewShotEnabled } = useOrgConfig(OrgConfigs.can_serve_datasets);
  const {
    data: syncStatus,
    error,
    isLoading,
  } = useGetSyncStatus(props.datasetId, {
    skip: !fewShotEnabled,
    refreshInterval: !fewShotEnabled ? 0 : 10000,
  });
  const isNotServed = Boolean(error?.message);

  return fewShotEnabled ? (
    isNotServed || !syncStatus ? (
      <FewShotSelector
        datasetId={props.datasetId}
        isLoading={isLoading}
        setEditDatasetOpen={props.setEditDatasetOpen}
      />
    ) : (
      <FewShotSearch
        datasetId={props.datasetId}
        inputSchema={props.inputSchema}
        syncStatus={syncStatus}
        onMutate={props.onMutate}
      />
    )
  ) : (
    <DisabledState />
  );
};

export { FewShotSearchPanel };
